import '../overrides/slate';
import { Editor, Text, createEditor } from 'slate';
import type {
  AnnotationV1,
  DocumentValue,
  DocumentValueV1,
  EditorMetadata,
} from '../types/aha-editor';
import {
  migrateData,
  type V1Node,
  migrateMarkData,
  type V1Mark,
  isV1TextNode,
  isV1Node,
  type V1TextNode,
} from './migrate_data';
import { type Descendant } from 'slate';
import Normalization from '../normalization';

/**
 * Implement the migration of the document structure from the v1 version
 * of the editor and the v2.
 */
const MIGRATION_VERSION = 0;

export const migrateMarks = (node: V1TextNode) => {
  if (!node.marks) return {};

  return node.marks.reduce<Record<string, any>>((markProps, mark: V1Mark) => {
    return {
      ...markProps,
      ...migrateMarkData(mark),
    };
  }, {});
};

export const migrateTextNodeToCodeSnippet = (
  text: string,
  markProps: Record<string, any>
): Descendant => {
  const { code: _, ...props } = markProps;

  return {
    type: 'code-snippet',
    children: [
      {
        text,
        ...props,
      },
    ],
  };
};

export const MigrateNodeToV2 = (node: V1Node | Descendant): Descendant => {
  // Migrate v1 nodes to v2 nodes.
  if (isV1Node(node)) {
    // Text nodes will never have children, so this is treated as the
    // base case of the recursive migrate function.
    // Mark elements are added at the top level of the node.
    if (isV1TextNode(node)) {
      const markProps = migrateMarks(node);
      const text =
        'text' in node
          ? node.text
          : node.leaves.map((leaf) => leaf.text).join('');

      // Special handling for code marks. These do not exist in v2
      // so we need to convert them to code-snippet elements.
      if (markProps.code) {
        return migrateTextNodeToCodeSnippet(text, markProps);
      }

      return {
        ...markProps,
        text,
      };
    }

    let children: Descendant[] = [{ text: '' }];
    if (node.nodes.length > 0) {
      children = Array.from(node.nodes.map(MigrateNodeToV2));
    }

    /**
     * Base case for non-text nodes, the node type is kept
     * and its children are migrated recursively.
     * Data elements are added at the top level of the node.
     */
    const dataProps = migrateData(node);
    return {
      // @ts-expect-error ignore this for now
      type: node.type,
      ...dataProps,
      children,
    };
  }

  // It's possible that a V2 node may end up with V1 nodes as children.
  // If this is the case, we need to recursively migrate the V2 node children.
  // Text nodes will just get returned as is.
  // Note that we can't use Element.isElement() here because it will return false
  // if any of the children are V1 nodes.
  if (
    !Text.isText(node) &&
    typeof node.type === 'string' &&
    Array.isArray(node.children)
  ) {
    node.children = node.children.map(MigrateNodeToV2);
  }

  return node;
};

export function MigrateAnnotationsToV2(
  annotations: EditorMetadata['annotations'],
  annotation: AnnotationV1
) {
  const {
    key,
    type,
    data: { resolved = false },
    anchor,
    focus,
  } = annotation;

  if (type !== 'aha-annotation') return annotations;

  const { object: anchorObject, ...anchorV2Props } = anchor;
  const { object: focusObject, ...focusV2Props } = focus;

  annotations[key] = {
    key,
    kind: 'comment',
    hidden: false,
    resolved,
    wasCut: false,
    range: {
      affinity: 'inward',
      current: {
        // Remove unnecessary properties.
        anchor: anchorV2Props,
        focus: focusV2Props,
      },
    },
  };
  return annotations;
}

export class MigrationError extends Error {}

/**
 * Entrypoint for the migration function, it receives a v1 editor
 * json value and returns an object with the current document version
 * adn the children array in v2 format.
 */
export const MigrateToV2 = (
  value: DocumentValueV1 | DocumentValue,
  force = false
): DocumentValue => {
  if (Array.isArray(value)) {
    throw new MigrationError('Invalid value provided: array');
  }

  let migrationResult;
  if ('document' in value) {
    const children = Array.from(value.document.nodes.map(MigrateNodeToV2));
    const metadata = {
      annotations: Object.values(value.annotations || {}).reduce(
        MigrateAnnotationsToV2,
        {}
      ),
      tableOfContentsEnabled: !!value.document.data?.tableOfContentsVisible,
      tableOfContentsCollapse: !!value.document.data?.tableOfContentsCollapse,
    };

    migrationResult = {
      documentVersion: MIGRATION_VERSION,
      // When the value is a Proxy, the result of `map` will also be
      // a proxied array which is not desirable, Array.from will unproxy to
      // a plain array.
      children,
      metadata,
    };
  }

  if ('children' in value && Array.isArray(value.children)) {
    migrationResult = {
      ...value,
      metadata: {
        annotations: {},
        tableOfContentsEnabled: false,
        tableOfContentsCollapse: false,
        ...(value.metadata || {}),
      },
    };

    if (force) {
      migrationResult.children = value.children.map(MigrateNodeToV2);
    }
  }

  if (!migrationResult) {
    throw new MigrationError("Couldn't recognize the document.");
  }

  const editor = createEditor();
  editor.children = migrationResult.children;
  editor.metadata = migrationResult.metadata;

  Normalization.initializeNormalization(editor);
  Editor.normalize(editor, { force: true });

  return {
    documentVersion: migrationResult.documentVersion ?? MIGRATION_VERSION,
    children: editor.children,
    metadata: editor.metadata,
  };
};
